import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useShipmentNotifications } from "~/features/shipment-management/hooks/use-shipment-notifications.ts";
import type { DispatchResponse } from "~/shared/schemas/shipment-management-schema.ts";
import { dispatchResponseSchema } from "~/shared/schemas/shipment-management-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { postAndValidate } from "~/shared/services/api/fetch-client.ts";

export type DispatchError = {
	attemptedAt: Date;
	message: string;
};

export const postApiDispatch = async ({ poNumber }: { poNumber: string }) => {
	const resp = await postAndValidate(
		apiRoutes.POSTDISPATCH,
		{ poNumber },
		dispatchResponseSchema,
	);

	if (resp.success) {
		return resp.data;
	}

	throw new Error(resp.error);
};

export const usePostApiDispatch = () => {
	const queryClient = useQueryClient();
	const { showSuccess } = useShipmentNotifications();
	const [dispatchError, setDispatchError] = useState<DispatchError | null>(
		null,
	);
	const clearDispatchError = () => setDispatchError(null);

	const { mutate, mutateAsync, isPending, error, data, reset } = useMutation<
		DispatchResponse,
		Error,
		string
	>({
		mutationFn: (poNumber: string) => {
			if (!poNumber) {
				throw new Error("PO Number is required");
			}
			return postApiDispatch({ poNumber });
		},
		onSuccess: (result) => {
			if (!result.success) {
				const messages = [
					...(result.validationErrors ?? []),
					...(result.errorMessage ? [result.errorMessage] : []),
				];

				setDispatchError({
					attemptedAt: new Date(),
					message:
						messages.length > 0
							? messages.join(" ")
							: "Failed to dispatch. Please try again.",
				});
				return;
			}

			showSuccess("Shipment dispatched.");
			// Keep the mutation pending until the shipment refetch completes so the
			// group's spinner overlay stays up through the state transition.
			return queryClient.invalidateQueries({ queryKey: ["shipment"] });
		},
		onError: (err) => {
			setDispatchError({
				attemptedAt: new Date(),
				message:
					err instanceof Error
						? err.message
						: "Failed to dispatch. Please try again.",
			});
		},
	});

	return {
		apiDispatch: mutate,
		apiDispatchAsync: mutateAsync,
		isSaving: isPending,
		error,
		data,
		resetError: reset,
		dispatchError,
		clearDispatchError,
	};
};
